import voucherReminderApi from './voucherReminderApi';
import {
  getVoucherReminderTemplateVariablesFallback,
  normalizeVrCmsLocale,
  VrReminderTemplateVariable,
  VrTemplateVarLocale,
} from './voucherReminderUtils';

const cache = new Map<VrTemplateVarLocale, VrReminderTemplateVariable[]>();
const inflight = new Map<VrTemplateVarLocale, Promise<VrReminderTemplateVariable[]>>();

function resolveDescription(
  raw: any,
  locale: VrTemplateVarLocale
): string {
  if (typeof raw === 'string') return raw.trim();
  if (raw && typeof raw === 'object') {
    return String(raw[locale] || raw.vi || raw.en || '').trim();
  }
  return '';
}

function normalizeVariables(
  raw: unknown,
  locale: VrTemplateVarLocale
): VrReminderTemplateVariable[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => ({
      key: String(item?.key || '').trim(),
      token: String(item?.token || '').trim(),
      description: resolveDescription(item?.description, locale),
    }))
    .filter((item) => item.key && item.token);
}

export async function fetchVoucherReminderTemplateVariables(
  localeInput?: string,
  force = false
): Promise<VrReminderTemplateVariable[]> {
  const locale = normalizeVrCmsLocale(localeInput);

  if (!force && cache.has(locale)) {
    return cache.get(locale)!;
  }

  const pending = inflight.get(locale);
  if (!force && pending) return pending;

  const request = voucherReminderApi
    .templateVariables(locale)
    .then((res) => {
      const list = normalizeVariables(res.data, locale);
      if (res.ok && list.length) {
        cache.set(locale, list);
        return list;
      }
      const fallback = getVoucherReminderTemplateVariablesFallback(locale);
      cache.set(locale, fallback);
      return fallback;
    })
    .catch(() => {
      const fallback = getVoucherReminderTemplateVariablesFallback(locale);
      cache.set(locale, fallback);
      return fallback;
    })
    .finally(() => {
      inflight.delete(locale);
    });

  inflight.set(locale, request);
  return request;
}

export function getCachedVoucherReminderTemplateVariables(
  localeInput?: string
): VrReminderTemplateVariable[] | null {
  const locale = normalizeVrCmsLocale(localeInput);
  return cache.get(locale) || null;
}
